import type {
  AdInputLookup,
  EnrichedAdInputField,
  ItemResponse,
  Violation,
} from '@finn-no/mobility-item-insertion-shared';
import { getAt } from '@finn-no/mobility-item-insertion-shared';
import { useLingui } from '@lingui/react';
import { getCategoryForAdViewType } from '@m10s/mobility-mapping-utils';
import { Alert } from '@warp-ds/react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, type SubmitErrorHandler, type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import type { Cfg } from '../../../types/config.js';
import type { WidgetModelArray } from '../../../types/widget.js';
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback.js';
import { useErrorTracking } from '../../hooks/useErrorTracking.js';
import { useFormReadyFlag } from '../../hooks/useFormReadyFlag.js';
import { useHotjar } from '../../hooks/useHotjar.js';
import { useItemUpdateMutation } from '../../hooks/useItemUpdater.js';
import { useMessageBus } from '../../hooks/useMessageBus.js';
import { usePulse } from '../../hooks/usePulse.js';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning.js';
import { isFieldDirty } from '../../utils/dirtyFieldsHelper.js';
import { getHuckConfig } from '../../utils/getHuckConfig.js';
import { getInitialValues } from '../../utils.js';
import { StaleItemModal } from '../modals/stale-item.js';
import { PreviewButton } from '../preview-button.js';
import { TermsAndConditions } from '../terms-and-conditions.js';
import { WidgetFactory } from '../widget-factory.js';
import { useFocusFirstError } from './hooks/useFocusFirstError.js';
import { useWizard, WizardProvider } from './WizardContext.js';
import { WizardNavigation } from './WizardNavigation.js';
import { WizardProgress } from './WizardProgress.js';

interface WizardFormContentProps {
  config: Cfg;
  initialStep: number;
  offset: number;
}

// Helper to check if a violation should be handled by client or server validation
const isValidatedRemotely = (violation: Violation, adInputLookup: AdInputLookup) => {
  if (violation.field === 'image') return true;

  const dotNotationPath = violation.field.replace(/\./g, '.children.');
  const modelField = getAt<EnrichedAdInputField>(adInputLookup, dotNotationPath);

  if (!modelField) return false;
  return !modelField.rules || modelField.rules?.every((rule) => rule.type !== violation.type);
};

// Helper to filter violations to only those in the current step
const filterViolationsByFields = (violations: Violation[], fields: Set<string>): Violation[] => {
  return violations.filter((violation) => fields.has(violation.field));
};

const WizardFormContent: React.FC<WizardFormContentProps> = ({ config, initialStep, offset }) => {
  const { currentStep, goToStep, isLastStep } = useWizard();
  const navigate = useNavigate();
  const { step: stepFromRoute } = useParams<{ step: string }>();
  const { i18n } = useLingui();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showStaleItemModal, setShowStaleItemModal] = useState(false);
  const [alert, setAlert] = useState<{ type: 'positive' | 'negative'; message: string } | null>(null);
  const remoteViolationsRef = useRef<Violation[]>([]);
  const hasStepSubmitAttemptedRef = useRef(false);
  const isMounted = useRef(false);
  const { trackPageView, trackClick } = usePulse(config.item);
  const { trackViolationErrors, trackValidationErrors, convertFieldErrorsToErrorInfo } = useErrorTracking({
    category: getCategoryForAdViewType(config.item.model),
  });

  const latestItemData = useRef<ItemResponse['data']>(
    getInitialValues({
      adInputLookup: config.adInputLookup,
      item: config.item,
      brand: config.brand,
      widgetModel: config.widgetModel.flat(),
    }) as unknown as ItemResponse['data'],
  );

  const methods = useForm<ItemResponse['data']>({
    values: latestItemData.current,
    criteriaMode: 'all',
    mode: 'onBlur',
  });

  const updateItem = useItemUpdateMutation(config.item?.etag ?? '');

  useFormReadyFlag();

  // Subscribe to formState.dirtyFields by reading it during render
  // This is required for React Hook Form's Proxy to track updates
  const { isDirty, dirtyFields } = methods.formState;
  const isFormDirty = isDirty && config.mode === 'edit';

  useUnsavedChangesWarning({ isDirty: isFormDirty, isSubmitting });
  useMessageBus(methods);
  useHotjar();

  const scrollToTop = () => {
    document.documentElement.scrollTo({ top: 0 });
  };

  // Extract all field names from current step's widget model
  const currentStepModel = config.widgetModel[currentStep] as WidgetModelArray;
  const currentStepFields = new Set<string>();
  const extractFieldNames = (widgets: WidgetModelArray, prefix = '') => {
    for (const widget of widgets) {
      const fullFieldName = prefix ? `${prefix}.${widget.name}` : widget.name;
      currentStepFields.add(fullFieldName);

      if (widget.type === 'group' && widget.children) {
        extractFieldNames(widget.children, fullFieldName);
      }
    }
  };
  extractFieldNames(currentStepModel);

  const { focusFirstError } = useFocusFirstError({
    methods,
    currentStepFields,
    currentStepModel,
  });

  // Sync remote violations to form errors - only for current step fields
  const syncViolationsToFormErrors = (options: { showAllErrors?: boolean } = {}) => {
    const { showAllErrors = false } = options;
    const relevantViolations = filterViolationsByFields(remoteViolationsRef.current, currentStepFields);

    for (const violation of relevantViolations) {
      if (isValidatedRemotely(violation, config.adInputLookup)) {
        // Only set error if submit was attempted (showAllErrors) OR field is dirty
        if (showAllErrors || isFieldDirty(violation.field, dirtyFields as Record<string, unknown>)) {
          // @ts-expect-error types need some adjustment
          methods.setError(violation.field, {
            type: violation.type,
            message: violation.message,
          });
        }
      }
    }
  };

  // Initialize wizard with step from URL only once
  useEffect(() => {
    if (hasInitialized) return;
    const stepIndexWithinRange = Math.max(0, Math.min(initialStep, config.widgetModel.length - 1));
    if (initialStep !== stepIndexWithinRange) {
      navigate(`/${config.itemId}/steps/${stepIndexWithinRange + offset}`, { replace: true });
      setHasInitialized(true);
      return;
    }
    goToStep(initialStep);
    trackPageView();
    setHasInitialized(true);
  }, [initialStep]);

  // Handle step changes from URL
  useEffect(() => {
    if (!hasInitialized) return;
    trackPageView();
    scrollToTop();
    const nextStepIndex = Number(stepFromRoute) - offset;
    goToStep(nextStepIndex);
  }, [stepFromRoute]);

  // Update form with latest data when step changes
  useEffect(() => {
    if (hasInitialized) {
      methods.reset(latestItemData.current);
      hasStepSubmitAttemptedRef.current = false;
      // Don't sync violations here - form is fresh after reset
      // Violations will be shown when user interacts with fields or attempts to proceed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, methods, hasInitialized]);

  // Unified save function - handles validation, saving, and violation management
  // Note: This only STORES violations, it does NOT sync them to form errors
  // Call syncViolationsToFormErrors() explicitly where violations should be displayed
  const saveFormData = async (options: {
    validate?: boolean;
    commit?: boolean;
    updateLatestData?: boolean;
  }): Promise<{ success: boolean; hasCurrentStepViolations?: boolean }> => {
    const { validate = false, commit = false, updateLatestData = false } = options;

    // Validate if requested
    if (validate) {
      const isValid = await methods.trigger();
      if (!isValid) {
        return { success: false };
      }
    }

    const data = methods.getValues();
    try {
      const result = await updateItem({ data, commit });

      // Store violations (but don't sync to form errors yet)
      if (result?.violations) {
        remoteViolationsRef.current = result.violations;
      } else {
        remoteViolationsRef.current = [];
      }

      // Update latest data if requested
      if (updateLatestData && result?.data) {
        latestItemData.current = result.data;
      }

      // Check if current step has violations (only relevant for non-commit saves)
      if (!commit && result?.violations) {
        const currentStepViolations = filterViolationsByFields(result.violations, currentStepFields);
        return { success: true, hasCurrentStepViolations: currentStepViolations.length > 0 };
      }

      return { success: true, hasCurrentStepViolations: false };
    } catch (error) {
      if ((error as { cause?: number }).cause === 412) {
        setShowStaleItemModal(true);
      }
      return { success: false };
    }
  };

  const debouncedHandleAutoSave = useDebouncedCallback(async () => {
    await saveFormData({ commit: false });
    // Show errors for dirty fields, or all if user has attempted to proceed
    syncViolationsToFormErrors({ showAllErrors: hasStepSubmitAttemptedRef.current });
  }, 2000);

  // Auto-save on form changes (create mode only)
  useEffect(() => {
    if (config.mode !== 'edit') {
      const cb = methods.subscribe({
        formState: { values: true },
        callback: () => {
          if (!isMounted.current) {
            isMounted.current = true;
            return;
          }
          debouncedHandleAutoSave();
        },
      });
      return () => cb();
    }
  }, [methods.subscribe, debouncedHandleAutoSave, config.mode]);

  // Save progress on step change - validates current step and saves without commit
  const saveProgress = async (): Promise<boolean> => {
    hasStepSubmitAttemptedRef.current = true;
    setIsSubmitting(true);
    setAlert(null);

    // First validate client-side
    const isValid = await methods.trigger();

    if (!isValid) {
      // Client validation failed - call handleInvalidFormSubmission for consistent error handling
      await handleInvalidFormSubmission(methods.formState.errors);
      focusFirstError(remoteViolationsRef.current);
      setIsSubmitting(false);
      return false;
    }

    // Client validation passed - save with data update
    const result = await saveFormData({ commit: false, updateLatestData: true });

    if (result.hasCurrentStepViolations) {
      // Current step has server violations - sync and block navigation
      syncViolationsToFormErrors({ showAllErrors: true });
      const currentStepViolations = filterViolationsByFields(remoteViolationsRef.current, currentStepFields);
      trackViolationErrors(currentStepViolations);
      focusFirstError(remoteViolationsRef.current);
      setIsSubmitting(false);
      return false;
    }

    setIsSubmitting(false);
    return true;
  };

  const handleValidFormSubmission: SubmitHandler<ItemResponse['data']> = async (formData) => {
    hasStepSubmitAttemptedRef.current = true;
    setIsSubmitting(true);
    try {
      trackClick(
        {
          intent: config.mode === 'create' ? 'Create' : 'Save',
          object: {
            elementType: 'Button',
          },
        },
        { data: formData },
      );

      const data = await updateItem({ data: formData, commit: true });

      const violations: Violation[] | undefined = data?.violations;

      if (violations && violations.length > 0) {
        remoteViolationsRef.current = violations;
        syncViolationsToFormErrors({ showAllErrors: true });
        trackViolationErrors(violations);
      } else {
        // Server accepted the data - update latestItemData so navigation
        // doesn't revert form to stale pre-save values
        if (data?.data) {
          latestItemData.current = data.data;
        }

        const params = new URLSearchParams({
          wizard: 'true',
          mode: config.mode,
          model: data?.model ?? '',
        });

        if (config.orgId) {
          params.set('orgId', config.orgId);
        }

        const redirectResponse = await fetch(`${config.root}/api/redirect/${config.itemId}?${params.toString()}`);

        const redirectData = await redirectResponse.json();
        if (redirectData.url) {
          window.location.assign(redirectData.url);
        }
      }
    } catch (e: unknown) {
      if ((e as { cause?: number }).cause === 412) {
        setShowStaleItemModal(true);
      } else {
        console.error('Unexpected error when submitting a valid form', e);
        methods.setError('root.serverError', {
          message: i18n.t({
            id: 'mobility-item-insertion.form.server.error',
            comment: 'Error message shown when a server error occurs during form submission',
            message: 'An unexpected error occurred. Please try again.',
          }),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInvalidFormSubmission: SubmitErrorHandler<ItemResponse['data']> = async (fieldErrors) => {
    hasStepSubmitAttemptedRef.current = true;
    await saveFormData({ commit: false });

    // Sync violations to form errors (user clicked submit, so show all errors)
    syncViolationsToFormErrors({ showAllErrors: true });

    // Track client-side validation errors - only for current step
    const clientErrors = convertFieldErrorsToErrorInfo(fieldErrors);
    const currentStepClientErrors = clientErrors.filter((error) => currentStepFields.has(error.field));
    trackValidationErrors(currentStepClientErrors);

    // Track server violations - only for current step
    const currentStepViolations = filterViolationsByFields(remoteViolationsRef.current, currentStepFields);
    trackViolationErrors(currentStepViolations);
  };

  const hasErrors = Object.keys(methods.formState.errors).length > 0;

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleValidFormSubmission, handleInvalidFormSubmission)}>
        <main className={`max-w-[700] mx-auto ${hasErrors ? 'pb-[164]' : 'pb-[130]'}`}>
          <WizardProgress numberOfSteps={config.widgetModel.length} hasVehicleLookup={config.initialStepNumberIs2} />
          <div className="min-h-[400]">
            <WidgetFactory widgetModel={currentStepModel} adInputLookup={config.adInputLookup} />
          </div>

          {alert && (
            <Alert type={alert.type} show={alert.message !== ''} className="mt-16">
              <p>{alert.message}</p>
            </Alert>
          )}

          <PreviewButton />
          {isLastStep && <TermsAndConditions className="text-center pt-32 mb-24" />}
        </main>
        <WizardNavigation
          onNext={saveProgress}
          onPrev={async () => {
            const result = await saveFormData({ updateLatestData: true });
            return result.success;
          }}
          onFinish={() => methods.handleSubmit(handleValidFormSubmission, handleInvalidFormSubmission)()}
          isLoading={isSubmitting}
          hasVehicleLookup={config.initialStepNumberIs2}
          adInputLookup={config.adInputLookup}
          widgetModel={config.widgetModel.flat()}
          numberOfSteps={config.widgetModel.length}
          enableBackToLookup={config.toggles.mobilityItemInsertionLookupBackNavigation && config.mode === 'create'}
        />
      </form>
      <StaleItemModal open={showStaleItemModal} onDismiss={() => setShowStaleItemModal(false)} />
    </FormProvider>
  );
};

export const WizardForm: React.FC = () => {
  const config = getHuckConfig();
  const { step } = useParams<{ step: string }>();
  const offset = config.initialStepNumberIs2 ? 2 : 1;
  const currentStepIndexFromUrl = step ? parseInt(step, 10) - offset : 0;
  return (
    <WizardProvider totalSteps={config.widgetModel.length} initialData={{}}>
      <WizardFormContent config={config} initialStep={currentStepIndexFromUrl} offset={offset} />
    </WizardProvider>
  );
};
